import {
  DiscountCodeService as NpmDiscountCodeService,
  InitWithAmplify,
  InitEnvironmentVariable,
} from '@PikaDoApp/npm-pikado-logic';
import type {
  NewDiscountCode,
  NewCreateDiscountCodeInput,
  NewUpdateDiscountCodeInput,
  NewGetAllDiscountCodesResponse,
  NewGetDiscountCodeByIdResponse,
  NewCreateDiscountCodeResponse,
  NewUpdateDiscountCodeResponse,
  NewDeleteDiscountCodeResponse,
  NewDiscountCounterResponse,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/discountCode/graphql/discountCode.interface';
import {Amplify} from 'aws-amplify';
import {getEnvironment} from '../../lib/getEnvironment';

// ── Response shapes ────────────────────────────────────────────────────────────

export interface DiscountCodesPaginatedResponse {
  items: NewDiscountCode[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface DiscountCounterResult {
  originalPrice: number;
  discountValue: number;
  discountType: string;
  currency: string;
  finalPrice: number;
  discountCodeId: string;
  curriculumId?: string;
  levelId?: string;
  packageId: string;
}

// ── Service ────────────────────────────────────────────────────────────────────

export class DiscountCodeService {
  private readonly service: NpmDiscountCodeService;

  constructor() {
    InitWithAmplify(Amplify);
    InitEnvironmentVariable(getEnvironment());
    this.service = new NpmDiscountCodeService();
  }

  // ── LEGACY QUERIES (for Partner features) ──────────────────────────────────
  async getSubscriptionsByDiscountCode(codeName: string) {
    const {DiscountCodeRepository} = await import('./DiscountCodeRepository');
    const repo = new DiscountCodeRepository();
    return repo.getSubscriptionsByDiscountCode(codeName);
  }

  async getDiscountCodesByPartner(nextToken: string, partnerName: string) {
    const {DiscountCodeRepository} = await import('./DiscountCodeRepository');
    const repo = new DiscountCodeRepository();
    return repo.getDiscountCodesByPartner(nextToken, partnerName);
  }

  // ── READ (all) ─────────────────────────────────────────────────────────────

  /** Fetch a paginated list of all discount codes. */
  async getAllDiscountCodes(
    page: number = 1,
    limit: number = 20,
    search?: string,
  ): Promise<DiscountCodesPaginatedResponse> {
    try {
      const response: NewGetAllDiscountCodesResponse =
        await this.service.newGetAllDiscountCodes(page, limit, search);

      const {
        data,
        total,
        page: p,
        limit: l,
        totalPages,
      } = response.getAllDiscountCodes;

      return {items: data ?? [], total, page: p, limit: l, totalPages};
    } catch (error) {
      console.error('[DiscountCodeService] getAllDiscountCodes:', error);
      throw error;
    }
  }

  // ── READ (one) ─────────────────────────────────────────────────────────────

  /** Fetch a single discount code by its MongoDB `_id`. */
  async getDiscountCodeById(id: string): Promise<NewDiscountCode> {
    try {
      const response: NewGetDiscountCodeByIdResponse =
        await this.service.newGetDiscountCodeById(id);

      return response.getDiscountCodeById;
    } catch (error) {
      console.error('[DiscountCodeService] getDiscountCodeById:', error);
      throw error;
    }
  }

  // ── CREATE ─────────────────────────────────────────────────────────────────

  /** Create a new discount code. */
  async createDiscountCode(
    input: NewCreateDiscountCodeInput,
  ): Promise<NewDiscountCode> {
    try {
      const response: NewCreateDiscountCodeResponse =
        await this.service.newCreateDiscountCode(input);

      return response.createDiscountCode;
    } catch (error: any) {
      console.error('[DiscountCodeService] createDiscountCode:', error);
      if (error?.errors?.length) {
        throw new Error(error.errors.map((e: any) => e.message).join(', '));
      }
      throw new Error(error?.message ?? 'Failed to create discount code.');
    }
  }

  // ── UPDATE ─────────────────────────────────────────────────────────────────

  /**
   * Update an existing discount code.
   * Pass `{ id, input: { ...fields } }` — only include fields you want to change.
   */
  async updateDiscountCode(
    input: NewUpdateDiscountCodeInput,
  ): Promise<NewDiscountCode> {
    try {
      const response: NewUpdateDiscountCodeResponse =
        await this.service.newUpdateDiscountCode(input);

      return response.updateDiscountCode;
    } catch (error: any) {
      console.error('[DiscountCodeService] updateDiscountCode:', error);
      if (error?.errors?.length) {
        throw new Error(error.errors.map((e: any) => e.message).join(', '));
      }
      throw new Error(error?.message ?? 'Failed to update discount code.');
    }
  }

  // ── DELETE ─────────────────────────────────────────────────────────────────

  /** Permanently delete a discount code by its MongoDB `_id`. */
  async deleteDiscountCode(id: string): Promise<NewDiscountCode> {
    try {
      const response: NewDeleteDiscountCodeResponse =
        await this.service.newDeleteDiscountCode(id);

      return response.deleteDiscountCode;
    } catch (error: any) {
      console.error('[DiscountCodeService] deleteDiscountCode:', error);
      throw new Error(error?.message ?? 'Failed to delete discount code.');
    }
  }

  // ── DISCOUNT CALCULATOR ────────────────────────────────────────────────────

  /**
   * Calculate the final price after applying a discount code.
   *
   * @param code       - The discount code string (e.g. "SUMMER25")
   * @param packageId  - The package ID the student is purchasing
   * @param country    - ISO-3166 country code (e.g. "US", "EG")
   * @param studentId  - The student's ID (used for per-student usage checks)
   */
  async discountCounter(
    code: string,
    packageId: string,
    country: string,
    studentId: string,
  ): Promise<DiscountCounterResult> {
    try {
      const response: NewDiscountCounterResponse =
        await this.service.newDiscountCounter(
          code,
          packageId,
          country,
          studentId,
        );

      return response.discountCounter;
    } catch (error: any) {
      console.error('[DiscountCodeService] discountCounter:', error);
      throw new Error(error?.message ?? 'Failed to calculate discount.');
    }
  }
}
